"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AcquisitionInvoker = void 0;
/*---------------------------------------------------------------------------------------------
*  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
*--------------------------------------------------------------------------------------------*/
const cp = require("child_process");
const os = require("os");
const path = require("path");
/* eslint-disable */ // When editing this file, please remove this and fix the linting concerns.
const EventStreamEvents_1 = require("../EventStream/EventStreamEvents");
const TelemetryUtilities_1 = require("../EventStream/TelemetryUtilities");
const CommandExecutor_1 = require("../Utils/CommandExecutor");
const ErrorHandler_1 = require("../Utils/ErrorHandler");
const FileUtilities_1 = require("../Utils/FileUtilities");
const InstallScriptAcquisitionWorker_1 = require("./InstallScriptAcquisitionWorker");
const TypescriptUtilities_1 = require("../Utils/TypescriptUtilities");
const WebRequestWorkerSingleton_1 = require("../Utils/WebRequestWorkerSingleton");
const DotnetConditionValidator_1 = require("./DotnetConditionValidator");
const DotnetCoreAcquisitionWorker_1 = require("./DotnetCoreAcquisitionWorker");
const IAcquisitionInvoker_1 = require("./IAcquisitionInvoker");
class AcquisitionInvoker extends IAcquisitionInvoker_1.IAcquisitionInvoker {
    constructor(workerContext, utilityContext) {
        super(workerContext.eventStream);
        this.workerContext = workerContext;
        this.utilityContext = utilityContext;
        this.noPowershellError = `powershell is not discoverable on your system. Is PowerShell added to your PATH and correctly installed? Please visit: https://aka.ms/install-powershell

You will need to restart VS Code after these changes. If PowerShell is still not discoverable, try setting a custom existingDotnetPath following our instructions here: https://github.com/dotnet/vscode-dotnet-runtime/blob/main/Documentation/troubleshooting-runtime.md.`;
        this.scriptWorker = new InstallScriptAcquisitionWorker_1.InstallScriptAcquisitionWorker(workerContext);
        this.fileUtilities = new FileUtilities_1.FileUtilities();
    }
    installDotnet(install) {
        return __awaiter(this, void 0, void 0, function* () {
            const installDir = this.workerContext.installDirectoryProvider.getInstallDir(install.installId);
            const winOS = os.platform() === 'win32';
            const installCommand = yield this.getInstallCommand(this.workerContext.acquisitionContext.version, installDir, this.workerContext.acquisitionContext.mode, this.workerContext.acquisitionContext.architecture);
            const dotnetPath = path.join(installDir, (0, TypescriptUtilities_1.getDotnetExecutable)());
            return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                try {
                    let powershellReference = 'powershell.exe';
                    let windowsFullCommand = `${powershellReference} -NoProfile -NonInteractive -NoLogo -ExecutionPolicy bypass -Command "& { [Net.ServicePointManager]::SecurityProtocol = [Net.ServicePointManager]::SecurityProtocol -bor [Net.SecurityProtocolType]::Tls12; & ${installCommand} }"`;
                    if (winOS) {
                        powershellReference = yield this.verifyPowershellCanRun(install);
                        windowsFullCommand = windowsFullCommand.replace('powershell.exe', powershellReference);
                    }
                    // The install script can leave behind a directory in an invalid install state. Make sure the executable is present at the very least.
                    if (yield this.fileUtilities.exists(installDir)) {
                        if (yield this.fileUtilities.exists(dotnetPath)) {
                            const validator = new DotnetConditionValidator_1.DotnetConditionValidator(this.workerContext, this.utilityContext);
                            const meetsRequirement = yield validator.dotnetMeetsRequirement(dotnetPath, { acquireContext: this.workerContext.acquisitionContext, versionSpecRequirement: 'equal' });
                            if (meetsRequirement) {
                                this.eventStream.post(new EventStreamEvents_1.DotnetAcquisitionCompleted(install, dotnetPath, this.workerContext.acquisitionContext.version));
                                return resolve();
                            }
                        }
                        try {
                            yield this.fileUtilities.wipeDirectory(installDir, this.eventStream, undefined, true);
                        }
                        catch (err) {
                            this.eventStream.post(new EventStreamEvents_1.SuppressedAcquisitionError(err, `${installDir} could not be not removed, and it has a corrupted install. Please remove it manually.`));
                        }
                    }
                    cp.exec(winOS ? windowsFullCommand : installCommand, { cwd: process.cwd(), maxBuffer: 500 * 1024, timeout: 1000 * this.workerContext.timeoutSeconds, killSignal: 'SIGKILL' }, (error, stdout, stderr) => __awaiter(this, void 0, void 0, function* () {
                        var _a;
                        if (stdout) {
                            this.eventStream.post(new EventStreamEvents_1.DotnetAcquisitionScriptOutput(install, TelemetryUtilities_1.TelemetryUtilities.HashAllPaths(stdout)));
                        }
                        if (stderr) {
                            this.eventStream.post(new EventStreamEvents_1.DotnetAcquisitionScriptOutput(install, `STDERR: ${TelemetryUtilities_1.TelemetryUtilities.HashAllPaths(stderr)}`));
                        }
                        if (this.looksLikeBadExecutionPolicyError(stderr)) {
                            const badPolicyError = new EventStreamEvents_1.EventBasedError('PowershellBadExecutionPolicy', `Your powershell execution policy does not allow script execution, so we can't automate the installation.
Please read more at https://go.microsoft.com/fwlink/?LinkID=135170`);
                            this.eventStream.post(new EventStreamEvents_1.PowershellBadExecutionPolicy(badPolicyError, install));
                            reject(badPolicyError);
                        }
                        if ((this.looksLikeBadLanguageModeError(stderr) || (error === null || error === void 0 ? void 0 : error.code) === 1) && (yield this.badLanguageModeSet(powershellReference))) {
                            const badModeError = new EventStreamEvents_1.EventBasedError('PowershellBadLanguageMode', `Your Language Mode disables PowerShell language features needed to install .NET. Read more at: https://learn.microsoft.com/powershell/module/microsoft.powershell.core/about/about_language_modes.
If you cannot change this flag, try setting a custom existingDotnetPath via the instructions here: https://github.com/dotnet/vscode-dotnet-runtime/blob/main/Documentation/troubleshooting-runtime.md.`);
                            this.eventStream.post(new EventStreamEvents_1.PowershellBadLanguageMode(badModeError, install));
                            reject(badModeError);
                        }
                        if (error) {
                            if (!(yield WebRequestWorkerSingleton_1.WebRequestWorkerSingleton.getInstance().isOnline(this.workerContext.timeoutSeconds, this.eventStream))) {
                                const offlineError = new EventStreamEvents_1.EventBasedError('DotnetOfflineFailure', 'No internet connection detected: Cannot install .NET');
                                this.eventStream.post(new EventStreamEvents_1.DotnetOfflineFailure(offlineError, install));
                                reject(offlineError);
                            }
                            else if (error.signal === 'SIGKILL') {
                                const newError = new EventStreamEvents_1.EventBasedError('DotnetAcquisitionTimeoutError', `${ErrorHandler_1.timeoutConstants.timeoutMessage}, MESSAGE: ${error.message}, CODE: ${error.code}, KILLED: ${error.killed}`, error.stack);
                                this.eventStream.post(new EventStreamEvents_1.DotnetAcquisitionTimeoutError(error, install, this.workerContext.timeoutSeconds));
                                reject(newError);
                            }
                            else {
                                const newError = new EventStreamEvents_1.EventBasedError('DotnetAcquisitionInstallError', `${ErrorHandler_1.timeoutConstants.timeoutMessage}, MESSAGE: ${error.message}, CODE: ${error.code}, SIGNAL: ${error.signal}`, error.stack);
                                this.eventStream.post(new EventStreamEvents_1.DotnetAcquisitionInstallError(newError, install));
                                reject(newError);
                            }
                        }
                        else if (((_a = stderr === null || stderr === void 0 ? void 0 : stderr.length) !== null && _a !== void 0 ? _a : 0) > 0) {
                            this.eventStream.post(new EventStreamEvents_1.DotnetAcquisitionCompleted(install, dotnetPath, this.workerContext.acquisitionContext.version));
                            resolve();
                        }
                        else {
                            this.eventStream.post(new EventStreamEvents_1.DotnetAcquisitionCompleted(install, dotnetPath, this.workerContext.acquisitionContext.version));
                            resolve();
                        }
                    }));
                }
                catch (error) {
                    // Remove this when https://github.com/typescript-eslint/typescript-eslint/issues/2728 is done
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    const newError = new EventStreamEvents_1.EventBasedError('DotnetAcquisitionUnexpectedError', error === null || error === void 0 ? void 0 : error.message, error === null || error === void 0 ? void 0 : error.stack);
                    this.eventStream.post(new EventStreamEvents_1.DotnetAcquisitionUnexpectedError(newError, install));
                    reject(newError);
                }
            }));
        });
    }
    looksLikeBadExecutionPolicyError(stderr) {
        // tls12 is from the command output and gets truncated like so with this error
        // 135170 is the link id to the error, which may be subject to change but is a good language agnostic way to catch this
        // about_Execution_Policies this is a relatively language agnostic way to check as well
        return stderr.includes('+ ... ]::Tls12;') || stderr.includes('135170') || stderr.includes('about_Execution_Policies');
    }
    looksLikeBadLanguageModeError(stderr) {
        /*
This is one possible output of a failed command for this right now, but the install script might change so we account for multiple possibilities.

        Failed to resolve the exact version number.
At dotnet-install.ps1:1189 char:5
+     throw "Failed to resolve the exact version number."
+     ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    + CategoryInfo          : OperationStopped: (Failed to resol...version number.:String) [], RuntimeException
    + FullyQualifiedErrorId : Failed to resolve the exact version number.
        */
        // Unexpected Token may also appear
        return stderr.includes('FullyQualifiedErrorId') || stderr.includes('unexpectedToken');
    }
    badLanguageModeSet(powershellReference) {
        return __awaiter(this, void 0, void 0, function* () {
            if (os.platform() !== 'win32') {
                return false;
            }
            try {
                const checkLanguageModeCmd = CommandExecutor_1.CommandExecutor.makeCommand(powershellReference, [`-command`, `$ExecutionContext.SessionState.LanguageMode`]);
                const languageModeOutput = yield new CommandExecutor_1.CommandExecutor(this.workerContext, this.utilityContext).execute(checkLanguageModeCmd, { cwd: path.resolve(__dirname), shell: true });
                const languageMode = languageModeOutput.stdout.trim();
                return (languageMode === 'ConstrainedLanguage' || languageMode === 'NoLanguage');
            }
            catch (e) {
                return true;
            }
        });
    }
    getInstallCommand(version, dotnetInstallDir, installMode, architecture) {
        return __awaiter(this, void 0, void 0, function* () {
            const arch = this.fileUtilities.nodeArchToDotnetArch(architecture !== null && architecture !== void 0 ? architecture : DotnetCoreAcquisitionWorker_1.DotnetCoreAcquisitionWorker.defaultArchitecture(), this.eventStream);
            let args = [
                '-InstallDir', this.escapeFilePath(dotnetInstallDir),
                '-Version', version,
                '-Verbose'
            ];
            if (installMode === 'runtime' || !installMode) {
                args = args.concat('-Runtime', 'dotnet');
            }
            else if (installMode === 'aspnetcore') {
                args = args.concat('-Runtime', 'aspnetcore');
            }
            if (arch !== 'auto') {
                args = args.concat('-Architecture', arch);
            }
            const scriptPath = yield this.scriptWorker.getDotnetInstallScriptPath();
            return `${this.escapeFilePath(scriptPath)} ${args.join(' ')}`;
        });
    }
    escapeFilePath(pathToEsc) {
        if (os.platform() === 'win32') {
            // Need to escape apostrophes with two apostrophes
            const dotnetInstallDirEscaped = pathToEsc.replace(/'/g, `''`);
            // Surround with single quotes instead of double quotes (see https://github.com/dotnet/cli/issues/11521)
            return `'${dotnetInstallDirEscaped}'`;
        }
        else {
            return `"${pathToEsc}"`;
        }
    }
    /**
     *
     * @remarks Some users have reported not having powershell.exe or having execution policy that fails property evaluation functions in powershell install scripts.
     * We use this function to throw better errors if powershell is not configured correctly.
     */
    verifyPowershellCanRun(installId) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            let knownError = false;
            let error = null;
            let command = null;
            const possibleCommands = // create a bunch of commands that just return the index of the correct shell in powershell
             [
                CommandExecutor_1.CommandExecutor.makeCommand('0', []),
                CommandExecutor_1.CommandExecutor.makeCommand('1', []),
                CommandExecutor_1.CommandExecutor.makeCommand('2', []),
                CommandExecutor_1.CommandExecutor.makeCommand('3', []),
                CommandExecutor_1.CommandExecutor.makeCommand('4', []),
            ];
            const possiblePowershellPaths = [
                { shell: `powershell.exe` },
                { shell: `%SystemRoot%\\System32\\WindowsPowerShell\\v1.0\\powershell.exe` },
                { shell: `pwsh` },
                { shell: `powershell` }
            ];
            try {
                // Check if PowerShell exists and is on the path.
                command = yield new CommandExecutor_1.CommandExecutor(this.workerContext, this.utilityContext).tryFindWorkingCommand(possibleCommands, possiblePowershellPaths);
                if (!command) {
                    knownError = true;
                    const err = Error(this.noPowershellError);
                    error = err;
                }
            }
            catch (err) {
                if (!knownError) {
                    error = new Error(`${this.noPowershellError} More details: ${err.message}`);
                }
            }
            if (error != null) {
                this.eventStream.post(new EventStreamEvents_1.DotnetAcquisitionScriptError(error, installId));
                throw new EventStreamEvents_1.EventBasedError('DotnetAcquisitionScriptError', error === null || error === void 0 ? void 0 : error.message, error === null || error === void 0 ? void 0 : error.stack);
            }
            return (_b = (_a = possiblePowershellPaths.at(Number(command.commandRoot))) === null || _a === void 0 ? void 0 : _a.shell) !== null && _b !== void 0 ? _b : 'powershell.exe';
        });
    }
}
exports.AcquisitionInvoker = AcquisitionInvoker;
//# sourceMappingURL=AcquisitionInvoker.js.map